import React, { useState } from "react";
import { IconButton, Button, CircularProgress, Backdrop } from "@mui/material";
import { DataTable } from "../../components/DataTable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Column } from "../../types/Column";
import EditModal from "../../components/EditModal";
import { LawGarant } from "./Law";
import DeleteDialog from "../../components/DeleteDialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addNewGarantLaw,
  fetchGarantLawById,
  fetchGarantLaws,
  fetchGarantLawByTopic,
  removeGarantLaw,
  updateGarantLaw,
} from "../../api";
import AddModal from "../../components/AddModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewModal from "../../components/ViewModal";
import { SearchDropdown } from "./SearchDropdown";

// Dummy user data - replace with actual data fetching logic
const LawsGarantPage = () => {
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
  const [currentdata, setCurrentdata] = useState<LawGarant | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [tokens, setTokens] = useState<string[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [searchTopic, setSearchTopic] = useState<number | null>(null);

  const handleEditClick = (data: LawGarant) => {
    setCurrentdata(data);
    setEditModalOpen(true);
  };

  const handleRemoveClick = (data: LawGarant) => {
    setCurrentdata(data);
    setRemoveModalOpen(true);
  };

  // Handler for when the modal is closed without saving
  const handleClose = () => {
    setEditModalOpen(false);
    setRemoveModalOpen(false);
    setAddModalOpen(false);
    setViewModalOpen(false);
    setCurrentdata(null);
    setSearchTopic(null);
  };

  // Handler for when the modal save action is performed
  const handleSave = (updateddata: LawGarant) => {
    changeLaw.mutate(updateddata);
    handleClose();
  };

  const handleAdd = (updateddata: LawGarant) => {
    addLaw.mutate({
      ...updateddata,

      topic: +updateddata.topic,
      display_order: +updateddata.display_order,
    });
    handleClose();
  };

  const handleConfirmRemove = () => {
    if (!currentdata) return;
    deleteLaw.mutate({ id: currentdata.id });
    handleClose();
  };

  const handleViewClick = (data: LawGarant) => {
    setCurrentdata(data);
    setViewModalOpen(true);
  };

  const columns: Column<LawGarant>[] = [
    {
      label: "id",
      render: (law) => <>{law.id}</>,
    },
    {
      label: "Название",
      render: (law) => <>{law.title}</>,
    },
    {
      label: "Тема",
      render: (law) => <>{law.topic}</>,
    },
    {
      label: "Действия",
      render: (law) => (
        <>
          <IconButton
            aria-label="edit"
            onClick={() => handleEditClick(law)}
            key={law.id}
          >
            <EditIcon />
          </IconButton>
          <IconButton aria-label="block" onClick={() => handleRemoveClick(law)}>
            <DeleteIcon />
          </IconButton>
          <IconButton aria-label="view" onClick={() => handleViewClick(law)}>
            <VisibilityIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const queryClient = useQueryClient();

  const { data, error, isLoading } = useQuery({
    queryKey: ["garant", rowsPerPage, nextPageToken],
    queryFn: () => fetchGarantLaws(rowsPerPage, nextPageToken),
  });

  const addLaw = useMutation({
    mutationFn: addNewGarantLaw,
    mutationKey: ["garant"],
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["garant"] }),
  });

  const deleteLaw = useMutation({
    mutationFn: removeGarantLaw,
    mutationKey: ["garant"],
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["garant"] }),
  });

  const changeLaw = useMutation({
    mutationFn: updateGarantLaw,
    mutationKey: ["garant"],
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["garant"] }),
  });

  const { data: law, isLoading: isLoadingLaw } = useQuery({
    queryKey: ["garant", currentdata?.id],
    queryFn: () => fetchGarantLawById(currentdata?.id ?? 0),
    enabled: !!currentdata?.id,
  });

  const { data: lawByTopic, isLoading: isLoadingByTopic } = useQuery({
    queryKey: ["garant", "topic", searchTopic],
    queryFn: () => fetchGarantLawByTopic(searchTopic ?? 0),
    enabled: !!searchTopic,
  });

  if (isLoading) return <div>Loading...</div>;

  if (error) return <div>Error: {error.message}</div>;

  const renderItems = (items: any[]) => {
    return (
      <div>
        {items?.map((item, index) => (
          <div
            key={index}
            style={{
              marginBottom: "16px",
              padding: "12px",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
            }}
          >
            <h4>Item {item.number}</h4>
            <div
              dangerouslySetInnerHTML={{ __html: item.text }}
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                padding: "8px",
                backgroundColor: "#f9f9f9",
                borderRadius: "4px",
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ paddingLeft: "20px", paddingRight: "10px" }}>
      <h1>Законодательные инициативы</h1>
      <div style={{ marginBottom: "20px", display: "flex" }}>
        <SearchDropdown
          onSelect={(topic) => {
            setSearchTopic(topic);
            setViewModalOpen(true);
          }}
        />
        <Button onClick={() => setAddModalOpen(true)}>+Добавить</Button>
      </div>
      {data && (
        <DataTable
          columns={columns}
          data={data.data}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(+event.target.value);
          }}
          page={page}
          handleChangePage={(event, newPage) => {
            setPage(newPage);
            if (newPage > page) {
              setNextPageToken(data.next_page_token);
              setTokens((prev) => [...prev, data.next_page_token]);
            } else {
              setNextPageToken(tokens[newPage - 1]);
            }
          }}
          count={data?.num_count}
        />
      )}{" "}
      <AddModal
        open={isAddModalOpen}
        onClose={handleClose}
        title="Добавить Законодательную инициативу"
        columns={[
          { label: "Название", key: "title", editable: true },
          { label: "Тема", key: "topic", editable: true },
          { label: "Порядок", key: "display_order", editable: true },
          { label: "Текст", key: "text", type: "file", editable: true },
        ]}
        onSave={handleAdd}
      />
      {currentdata && (
        <EditModal
          open={isEditModalOpen}
          onClose={handleClose}
          title="Редактирование законодательной инициативы"
          data={{ ...currentdata }}
          columns={[
            { label: "Название", key: "title", editable: true },
            { label: "Тема", key: "topic", editable: true },
            { label: "Текст", key: "text", type: "file", editable: true },
            { label: "Порядок", key: "display_order", editable: true },
          ]}
          onSave={handleSave}
        />
      )}
      {currentdata && (
        <DeleteDialog
          open={isRemoveModalOpen}
          onClose={handleClose}
          onConfirm={handleConfirmRemove}
          title="Удаление закона"
          text={`Вы точно хотите удалить Законодательную инициативу
        ${currentdata.title}?`}
        />
      )}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={(isLoadingLaw || isLoadingByTopic) && isViewModalOpen}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {(law || lawByTopic) && (
        <ViewModal
          open={isViewModalOpen}
          onClose={handleClose}
          title="Просмотр законодательной инициативы"
          data={law || lawByTopic!}
          columns={[
            {
              key: "items",
              render: (value) => (
                <div>
                  <p>
                    <strong>Всего статей: {value?.length}</strong>
                  </p>
                  {renderItems(value)}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default LawsGarantPage;
